import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, User, MapPin, Briefcase, Languages, FileCheck, Car, 
  Clock, Mail, Phone, MessageSquare, Loader2, Send, Calendar, Shield, CreditCard
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe import with fallback
let PERMESSI_LAVORO = [];
try {
  const data = require("../data/settoriSkills");
  PERMESSI_LAVORO = data.PERMESSI_LAVORO || [];
} catch (e) {
  console.error("Failed to load settoriSkills:", e);
}

export default function CandidateDetailPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchCandidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const fetchCandidate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/candidates/${candidateId}`);
      setCandidate(response.data);
    } catch (err) {
      console.error("Failed to fetch candidate:", err);
      if (err.response?.status === 404) {
        setError("Candidato non trovato");
      } else {
        setError("Errore nel caricamento del profilo");
      }
    } finally {
      setLoading(false);
    }
  };

  const parseLanguages = (lingueStr) => {
    if (!lingueStr) return [];
    try {
      const parsed = JSON.parse(lingueStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getPermessoLabel = (value) => {
    if (!value) return "";
    const permesso = (PERMESSI_LAVORO || []).find(p => p && p.value === value);
    return permesso ? permesso.label : value;
  };

  const getDisponibilitaLabel = (value) => {
    const labels = {
      "subito": "Subito",
      "2giorni": "2 giorni",
      "1settimana": "Una settimana",
      "2settimane": "Due settimane",
      "1mese": "Un mese",
      "2mesi": "Due mesi",
      "3mesi": "3 mesi"
    };
    return labels[value] || value || "";
  };

  const getPermessoSvizzeroLabel = (value) => {
    const labels = {
      "nessuno": "Nessun permesso",
      "L": "Permesso L (Soggiornante)",
      "B": "Permesso B (Residenza)",
      "C": "Permesso C (Domicilio)",
      "G": "Permesso G (Frontaliere)"
    };
    return labels[value] || value || "";
  };

  const handleContactClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    
    if (user.user_type !== "company" && user.user_type !== "admin") {
      toast.error("Solo le aziende possono contattare i candidati");
      return;
    }

    setContactForm({ 
      subject: `Opportunità lavorativa - ${user.company_name || user.name || "Azienda"}`,
      message: "" 
    });
    setShowContactModal(true);
  };

  const handleSendMessage = async () => {
    if (!contactForm.subject?.trim() || !contactForm.message?.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }

    if (!candidate?.id) {
      toast.error("Candidato non valido");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: candidate.id,
        subject: contactForm.subject,
        content: contactForm.message
      });
      toast.success("Messaggio inviato al candidato!");
      setShowContactModal(false);
      setContactForm({ subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio del messaggio");
    } finally {
      setSending(false);
    }
  };

  const lingue = candidate ? parseLanguages(candidate.lingue) : [];
  const esperienze = candidate?.esperienze || [];
  const patenti = candidate?.patenti || [];

  const canContact = user && (user.user_type === "company" || user.user_type === "admin");

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="candidate-detail-page">
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#004E64] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/candidates")}
            className="text-white hover:bg-white/10 mb-4"
            data-testid="back-to-candidates"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla ricerca
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <User className="w-12 h-12 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-700 mb-2">{error}</h3>
            <Button variant="outline" onClick={() => navigate("/candidates")}>
              Torna alla ricerca
            </Button>
          </div>
        )}

        {/* Candidate Profile */}
        {candidate && !loading && !error && (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-[#004E64] to-[#006B8A] p-6 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold">{candidate.name || "Candidato"}</h1>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-white/80">
                      {candidate.comune_residenza && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{candidate.comune_residenza}</span>
                        </div>
                      )}
                      {candidate.eta && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{candidate.eta} anni</span>
                        </div>
                      )}
                    </div>
                    {candidate.settore && (
                      <Badge className="mt-3 bg-white/20 text-white border-0">
                        {candidate.settore}
                      </Badge>
                    )}
                  </div>
                  {canContact && (
                    <Button 
                      onClick={handleContactClick}
                      className="bg-[#DA291C] hover:bg-[#B91C1C] text-white"
                      data-testid="contact-candidate-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contatta
                    </Button>
                  )}
                </div>
              </div>

              <CardContent className="p-6">
                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {candidate.disponibilita && (
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Clock className="w-5 h-5 text-[#004E64] mx-auto mb-2" />
                      <p className="text-xs text-slate-500">Disponibilità</p>
                      <p className="font-semibold text-slate-700">{getDisponibilitaLabel(candidate.disponibilita)}</p>
                    </div>
                  )}
                  {candidate.anni_esperienza && (
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Briefcase className="w-5 h-5 text-[#004E64] mx-auto mb-2" />
                      <p className="text-xs text-slate-500">Esperienza</p>
                      <p className="font-semibold text-slate-700">{candidate.anni_esperienza} anni</p>
                    </div>
                  )}
                  {candidate.has_auto && (
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Car className="w-5 h-5 text-[#004E64] mx-auto mb-2" />
                      <p className="text-xs text-slate-500">Mobilità</p>
                      <p className="font-semibold text-slate-700">Automunito</p>
                    </div>
                  )}
                  {candidate.permesso_lavoro && (
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <FileCheck className="w-5 h-5 text-[#004E64] mx-auto mb-2" />
                      <p className="text-xs text-slate-500">Permesso</p>
                      <p className="font-semibold text-slate-700 text-sm">{getPermessoLabel(candidate.permesso_lavoro)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Skills */}
            {candidate.skills && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#004E64]" />
                    Competenze
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {candidate.skills.split(" | ").filter(Boolean).map((skill) => (
                      <Badge key={`skill-${skill}`} variant="secondary" className="bg-[#004E64]/10 text-[#004E64]">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Esperienze */}
            {esperienze.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#004E64]" />
                    Esperienze Lavorative
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {esperienze.map((exp, i) => (
                      <div key={`exp-${exp.posizione || exp.titolo || i}-${i}`} className="border-l-2 border-[#004E64] pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-slate-800">{exp.posizione || exp.titolo || "Posizione"}</h4>
                          {(exp.anni || exp.anni_esperienza) && (
                            <Badge variant="outline" className="text-xs">
                              {exp.anni || exp.anni_esperienza} {(exp.anni || exp.anni_esperienza) === 1 ? "anno" : "anni"}
                            </Badge>
                          )}
                        </div>
                        {exp.descrizione && (
                          <p className="text-slate-600 mt-1 text-sm">{exp.descrizione}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lingue */}
            {lingue.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Languages className="w-5 h-5 text-[#004E64]" />
                    Lingue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {lingue.map((l) => (
                      <div key={`lang-${l.code}`} className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                        <span className="text-2xl">{l.code === "it" ? "🇮🇹" : l.code === "de" ? "🇩🇪" : l.code === "fr" ? "🇫🇷" : l.code === "en" ? "🇬🇧" : "🌐"}</span>
                        <div>
                          <p className="font-medium text-slate-700">{l.code?.toUpperCase() || "?"}</p>
                          <p className="text-xs text-slate-500">{l.level || "Base"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dati Anagrafici CH */}
            {(candidate.cittadinanza || candidate.permesso_svizzero || patenti.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#004E64]" />
                    Informazioni Burocratiche
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {candidate.cittadinanza && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 mb-1">Cittadinanza</p>
                        <p className="font-semibold text-slate-700">{candidate.cittadinanza}</p>
                      </div>
                    )}
                    {candidate.permesso_svizzero && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 mb-1">Permesso Svizzero</p>
                        <p className="font-semibold text-slate-700">{getPermessoSvizzeroLabel(candidate.permesso_svizzero)}</p>
                      </div>
                    )}
                    {patenti.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 mb-1">Patenti</p>
                        <div className="flex gap-2 flex-wrap">
                          {patenti.map((p) => (
                            <Badge key={`patente-${p}`} variant="outline">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Info (se disponibile) */}
            {(candidate.email || candidate.phone) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#004E64]" />
                    Contatti
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {candidate.email && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <a href={`mailto:${candidate.email}`} className="hover:text-[#004E64] hover:underline">
                          {candidate.email}
                        </a>
                      </div>
                    )}
                    {candidate.phone && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <a href={`tel:${candidate.phone}`} className="hover:text-[#004E64] hover:underline">
                          {candidate.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA per non loggati o non aziende */}
            {!canContact && (
              <Card className="border-[#DA291C]/30 bg-[#DA291C]/5">
                <CardContent className="p-6 text-center">
                  <MessageSquare className="w-10 h-10 text-[#DA291C] mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2">Vuoi contattare questo candidato?</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {!user 
                      ? "Accedi come azienda per inviare un messaggio a questo candidato."
                      : "Solo le aziende registrate possono contattare i candidati."}
                  </p>
                  {!user && (
                    <Button onClick={() => navigate("/login")} className="bg-[#DA291C] hover:bg-[#B91C1C]">
                      Accedi
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contatta {candidate?.name || "Candidato"}</DialogTitle>
            <DialogDescription>
              Invia un messaggio tramite il portale
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {candidate && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-[#004E64] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">{candidate.name || "Candidato"}</p>
                  <p className="text-sm text-slate-500">{candidate.comune_residenza || "Ticino"}</p>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Oggetto
              </label>
              <Input
                value={contactForm.subject || ""}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                placeholder="es. Opportunità lavorativa"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Messaggio
              </label>
              <Textarea
                value={contactForm.message || ""}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="Scrivi il tuo messaggio al candidato..."
                rows={5}
                data-testid="contact-message-input"
              />
            </div>

            <p className="text-xs text-slate-500">
              Il candidato riceverà il messaggio nella sua casella del portale.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={sending}
              className="bg-[#DA291C] hover:bg-[#B91C1C]"
              data-testid="send-message-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Invia messaggio</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
